/**
 * Elevate PrintWerks — design studio for the product page (sections/epw-product.liquid)
 * ---------------------------------------------------------------------------
 * Fabric.js canvas over a drawing of the garment. The customer uploads art or
 * adds text in one or more print spots (front, back, left chest, hat front),
 * approves the preview as their proof, and adds it to the cart.
 *
 * Artwork is sent with Shopify's own line-item file uploads: each design line
 * is posted to /cart/add.js as multipart FormData, and Shopify stores the file
 * and puts its URL in the property. Property names match what the ERP order
 * import reads: _Artwork_URL, Placement, _Placement_Code, Print_Width_Inches,
 * Print_Height_Inches, _Print_Method, _Design_Group_Id, _Line_Role,
 * _Proof_Approved_At, _Proof_Terms, _Proof_Mockup_URL, _Original_Artwork_URL.
 *
 * Units: each garment type has a scale (ppi = canvas px per printed inch;
 * 20 for clothing, 60 for headwear). An image's print DPI is ppi / scale.
 */
(function () {
  'use strict';

  var STAGE = 640;
  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  var ROTATE_STEP = 15;
  var AREA_SMALL = 16; // sq in: left chest, hat
  var AREA_MEDIUM = 64;
  var PRINT_METHOD = 'DTF Transfer';
  var SAYING_DPI = 300; // the saying print files are drawn at 300 dots per inch
  var SAYING_KEY = 'epw-saying'; // sessionStorage: a saying picked on the collection page

  function $(root, sel) { return root.querySelector(sel); }
  function $all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function json(root, sel, fallback) {
    var el = $(root, sel);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }
  function money(cents, fmt) {
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!fmt) return '$' + withCommas;
    return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withCommas);
  }
  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'epw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function luminance(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return 0.5;
    var n = parseInt(m[1], 16);
    var lin = function (c) { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  }
  function canvasToBlob(canvasEl, type, quality) {
    return new Promise(function (resolve, reject) {
      canvasEl.toBlob(function (b) { b ? resolve(b) : reject(new Error('Could not make the image file.')); }, type || 'image/png', quality);
    });
  }
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Could not load image.')); };
      img.src = src;
    });
  }
  function postForm(url, body) {
    return fetch(url, { method: 'POST', body: body, headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var msg = (data && (data.description || data.message)) || ('Cart error ' + res.status);
          throw new Error(msg);
        }
        return data;
      });
    });
  }

  function Designer(root) {
    this.root = root;
    this.ds = root.dataset;
    var zinfo = json(root, '[data-epw-zones]', { ppi: 20, zones: [] });
    this.ppi = zinfo.ppi || 20;
    this.zones = (zinfo.zones || []).filter(function (z) { return z && z.key; });
    if (!this.zones.length) this.zones = [{ key: 'FRONT', label: 'Front', code: 'FULL_FRONT', widthIn: 12, heightIn: 14, cx: 320, cy: 340, view: 'front' }];
    this.variants = json(root, '[data-epw-variants]', []);
    this.paints = json(root, '[data-epw-colors]', {});
    this.sayings = json(root, '[data-epw-sayings]', []).filter(function (x) { return x && x.slug && x.dark && x.light; });
    this.isSayingProduct = this.ds.sayingProduct === 'true';
    this.colorIndex = parseInt(this.ds.colorIndex, 10);
    this.sizeIndex = parseInt(this.ds.sizeIndex, 10);
    this.maxQty = Math.max(1, parseInt(this.ds.maxQty, 10) || 24);
    this.minDpi = parseInt(this.ds.minDpi, 10) || 300;
    this.moneyFormat = this.ds.moneyFormat || '${{amount}}';
    this.sur = { xl2: this.ds.surXlTwo, xl3: this.ds.surXlThree, small: this.ds.surS, medium: this.ds.surM, large: this.ds.surL };

    this.page = root.closest('.epw-pdp') || document;
    this.objects = {};
    this.zones.forEach(function (z) { this.objects[z.key] = []; }, this);
    this.activeZone = this.zones[0].key;
    this.groupId = uuid();
    this.color = null;
    this.size = null;
    this.ink = null;

    this.el = {
      stage: $(root, '[data-epw-stage]'),
      views: $all(root, '.epw-stage__view'),
      canvas: $(root, '[data-epw-canvas]'),
      dpi: $(root, '[data-epw-dpi]'),
      empty: $(root, '[data-epw-empty]'),
      tabs: $(root, '[data-epw-zone-tabs]'),
      file: $(root, '[data-epw-file]'),
      addText: $(root, '[data-epw-add-text]'),
      center: $(root, '[data-epw-center]'),
      bigger: $(root, '[data-epw-bigger]'),
      smaller: $(root, '[data-epw-smaller]'),
      rotate: $(root, '[data-epw-rotate]'),
      del: $(root, '[data-epw-delete]'),
      textbar: $(root, '[data-epw-textbar]'),
      font: $(root, '[data-epw-font]'),
      inks: $all(root, '[data-epw-ink]'),
      inkCustom: $(root, '[data-epw-ink-custom]'),
      swatches: $all(this.page, '[data-epw-color]'),
      colorName: $(this.page, '[data-epw-color-name]'),
      sizes: $(this.page, '[data-epw-sizes]'),
      qty: $(this.page, '[data-epw-qty]'),
      qtyMinus: $(this.page, '[data-epw-qty-minus]'),
      qtyPlus: $(this.page, '[data-epw-qty-plus]'),
      unit: $(this.page, '[data-epw-unit]'),
      pBase: $(this.page, '[data-epw-p-base]'),
      pSizeRow: $(this.page, '[data-epw-p-size-row]'),
      pSizeLabel: $(this.page, '[data-epw-p-size-label]'),
      pSize: $(this.page, '[data-epw-p-size]'),
      pExtraRow: $(this.page, '[data-epw-p-extra-row]'),
      pExtraLabel: $(this.page, '[data-epw-p-extra-label]'),
      pExtra: $(this.page, '[data-epw-p-extra]'),
      pTotal: $(this.page, '[data-epw-p-total]'),
      pTotalLabel: $(this.page, '[data-epw-p-total-label]'),
      proof: $(this.page, '[data-epw-proof]'),
      proofAck: $(this.page, '[data-epw-proof-ack]'),
      proofText: $(this.page, '[data-epw-proof-text]'),
      status: $(this.page, '[data-epw-status]'),
      add: $(this.page, '[data-epw-add]'),
      sayOpen: $(root, '[data-epw-sayings-open]'),
      sayPanel: $(root, '[data-epw-sayings-panel]'),
      sayClose: $(root, '[data-epw-sayings-close]'),
      sayGrid: $(root, '[data-epw-sayings-grid]'),
    };

    this.canvas = new fabric.Canvas(this.el.canvas, { preserveObjectStacking: true, selection: false, controlsAboveOverlay: true });
    fabric.Object.prototype.set({ borderColor: '#2E8BFF', cornerColor: '#2E8BFF', cornerStrokeColor: '#ffffff', cornerStyle: 'circle', transparentCorners: false, cornerSize: 14, borderScaleFactor: 2 });
    this.fit();
    var self = this;
    window.addEventListener('resize', function () { self.fit(); });

    this.buildTabs();
    this.wireColors();
    this.buildSizes();
    this.wireQty();
    this.wireTools();
    this.wireCanvas();
    this.wireSayings();
    this.selectZone(this.activeZone);
    this.refresh();
    this.preloadSaying();
  }

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------
  Designer.prototype.fit = function () {
    var w = this.el.stage.clientWidth || STAGE;
    this.canvas.setDimensions({ width: STAGE, height: STAGE });
    this.canvas.setDimensions({ width: w + 'px', height: w + 'px' }, { cssOnly: true });
    this.canvas.calcOffset();
  };

  Designer.prototype.zone = function (key) {
    for (var i = 0; i < this.zones.length; i++) if (this.zones[i].key === key) return this.zones[i];
    return this.zones[0];
  };
  Designer.prototype.rect = function (z) {
    var w = z.widthIn * this.ppi, h = z.heightIn * this.ppi;
    return { left: z.cx - w / 2, top: z.cy - h / 2, width: w, height: h };
  };
  Designer.prototype.isPrimary = function (key) { return this.zones[0].key === key; };

  Designer.prototype.buildTabs = function () {
    var self = this;
    this.el.tabs.innerHTML = '';
    if (this.zones.length < 2) { this.el.tabs.hidden = true; return; }
    this.zones.forEach(function (z) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'epw-zone';
      b.setAttribute('role', 'tab');
      b.dataset.zone = z.key;
      b.innerHTML = '<span class="epw-zone__name"></span><span class="epw-zone__meta"></span>';
      b.querySelector('.epw-zone__name').textContent = z.label;
      b.addEventListener('click', function () { self.selectZone(z.key); });
      self.el.tabs.appendChild(b);
    });
  };

  Designer.prototype.selectZone = function (key) {
    var self = this;
    this.activeZone = key;
    var z = this.zone(key);
    this.el.views.forEach(function (v) { v.hidden = v.getAttribute('data-view') !== z.view; });
    // Show artwork for every spot on the visible side; only the active spot is editable.
    Object.keys(this.objects).forEach(function (k) {
      var zk = self.zone(k);
      self.objects[k].forEach(function (o) {
        o.visible = zk.view === z.view;
        o.selectable = o.evented = k === key;
      });
    });
    this.canvas.discardActiveObject();
    this.drawGuide();
    $all(this.el.tabs, '.epw-zone').forEach(function (b) {
      var on = b.dataset.zone === key;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    var first = this.objects[key][this.objects[key].length - 1];
    if (first) this.canvas.setActiveObject(first);
    this.canvas.requestRenderAll();
    this.refresh();
  };

  Designer.prototype.drawGuide = function () {
    if (this.guide) this.canvas.remove(this.guide);
    if (this.guideLabel) this.canvas.remove(this.guideLabel);
    var z = this.zone(this.activeZone);
    var r = this.rect(z);
    var dark = luminance(this.paint().fill) < 0.35;
    this.guide = new fabric.Rect({
      left: r.left, top: r.top, width: r.width, height: r.height,
      fill: 'rgba(46,139,255,0.06)', stroke: dark ? 'rgba(255,255,255,0.7)' : 'rgba(46,139,255,0.9)',
      strokeDashArray: [7, 5], strokeWidth: 1.5, selectable: false, evented: false, epwGuide: true,
    });
    this.guideLabel = new fabric.Text(z.label + ' · ' + z.widthIn + '" × ' + z.heightIn + '" max', {
      left: r.left, top: r.top - 18, fontSize: 12, fontFamily: 'system-ui, sans-serif',
      fill: dark ? 'rgba(255,255,255,0.85)' : 'rgba(20,40,80,0.85)', selectable: false, evented: false, epwGuide: true,
    });
    this.canvas.add(this.guide);
    this.canvas.add(this.guideLabel);
    this.canvas.sendToBack(this.guideLabel);
    this.canvas.sendToBack(this.guide);
  };

  Designer.prototype.setGuidesVisible = function (on) {
    if (this.guide) this.guide.visible = on;
    if (this.guideLabel) this.guideLabel.visible = on;
  };

  // ---------------------------------------------------------------------------
  // Color, size, quantity
  // ---------------------------------------------------------------------------
  Designer.prototype.optionOf = function (v, index) { return index >= 0 ? v['option' + (index + 1)] : null; };

  Designer.prototype.paint = function () {
    var raw = (this.color && this.paints[this.color]) || '#9aa0a8|#9aa0a8';
    var p = raw.split('|');
    var fill = p[0], trim = p[1] || p[0], td = null;
    if (fill.indexOf('td:') === 0) { td = fill.slice(3).split(','); fill = td[0]; }
    return { fill: fill, trim: trim, td: td };
  };

  Designer.prototype.applyPaint = function () {
    var p = this.paint();
    this.el.views.forEach(function (v) {
      var svg = v.querySelector('svg');
      if (!svg) return;
      var grad = svg.querySelector('[data-td]');
      if (p.td && grad) {
        while (grad.firstChild) grad.removeChild(grad.firstChild);
        p.td.forEach(function (c, i) {
          var s = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
          s.setAttribute('offset', String(i / Math.max(1, p.td.length - 1)));
          s.setAttribute('stop-color', c);
          grad.appendChild(s);
        });
        svg.style.setProperty('--g-fill', 'url(#' + grad.id + ')');
      } else {
        svg.style.setProperty('--g-fill', p.fill);
      }
      svg.style.setProperty('--g-trim', p.trim);
    });
    if (this.guide) this.drawGuide();
  };

  Designer.prototype.wireColors = function () {
    var self = this;
    if (this.colorIndex < 0 || !this.el.swatches.length) { this.color = null; this.applyPaint(); return; }
    var available = {};
    this.variants.forEach(function (v) { if (v.available) available[self.optionOf(v, self.colorIndex)] = true; });
    this.el.swatches.forEach(function (b) {
      var c = b.getAttribute('data-epw-color');
      if (!available[c]) { b.disabled = true; b.classList.add('is-out'); }
      b.addEventListener('click', function () { self.setColor(c); });
    });
    var start = this.el.swatches.filter(function (b) { return !b.disabled; })[0];
    this.setColor(start ? start.getAttribute('data-epw-color') : this.el.swatches[0].getAttribute('data-epw-color'), true);
  };

  Designer.prototype.setColor = function (c, quiet) {
    this.color = c;
    this.el.swatches.forEach(function (b) {
      var on = b.getAttribute('data-epw-color') === c;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this.el.colorName) this.el.colorName.textContent = c;
    this.applyPaint();
    this.swapSayingInks();
    if (this.el.sizes) this.buildSizes();
    if (!quiet) this.withdrawProof();
    this.refresh();
  };

  Designer.prototype.variantsForColor = function () {
    var self = this;
    return this.variants.filter(function (v) { return self.colorIndex < 0 || self.optionOf(v, self.colorIndex) === self.color; });
  };

  Designer.prototype.buildSizes = function () {
    var self = this;
    if (!this.el.sizes || this.sizeIndex < 0) return;
    var keep = this.size;
    this.el.sizes.innerHTML = '';
    var list = this.variantsForColor();
    var firstAvail = null;
    list.forEach(function (v) {
      var s = self.optionOf(v, self.sizeIndex);
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'epw-size';
      b.textContent = s;
      b.dataset.size = s;
      if (!v.available) { b.disabled = true; b.classList.add('is-out'); }
      else if (!firstAvail) firstAvail = s;
      b.addEventListener('click', function () { self.setSize(s); });
      self.el.sizes.appendChild(b);
    });
    var has = list.some(function (v) { return v.available && self.optionOf(v, self.sizeIndex) === keep; });
    this.setSize(has ? keep : (list.some(function (v) { return v.available && self.optionOf(v, self.sizeIndex) === 'L'; }) ? 'L' : firstAvail));
  };

  Designer.prototype.setSize = function (s) {
    this.size = s;
    $all(this.el.sizes || document.createElement('div'), '.epw-size').forEach(function (b) {
      var on = b.dataset.size === s;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.refresh();
  };

  Designer.prototype.variant = function () {
    var self = this;
    var list = this.variantsForColor().filter(function (v) { return v.available; });
    if (this.sizeIndex >= 0) {
      for (var i = 0; i < list.length; i++) if (this.optionOf(list[i], this.sizeIndex) === this.size) return list[i];
    }
    return list[0] || null;
  };

  Designer.prototype.qty = function () {
    var n = parseInt(this.el.qty && this.el.qty.value, 10);
    if (!isFinite(n) || n < 1) n = 1;
    return Math.min(n, this.maxQty);
  };

  Designer.prototype.wireQty = function () {
    var self = this;
    if (!this.el.qty) return;
    this.el.qty.max = String(this.maxQty);
    this.el.qty.addEventListener('input', function () { self.refresh(); });
    this.el.qty.addEventListener('change', function () { self.el.qty.value = String(self.qty()); self.refresh(); });
    if (this.el.qtyMinus) this.el.qtyMinus.addEventListener('click', function () { self.el.qty.value = String(Math.max(1, self.qty() - 1)); self.refresh(); });
    if (this.el.qtyPlus) this.el.qtyPlus.addEventListener('click', function () { self.el.qty.value = String(Math.min(self.maxQty, self.qty() + 1)); self.refresh(); });
  };

  // ---------------------------------------------------------------------------
  // Tools
  // ---------------------------------------------------------------------------
  Designer.prototype.wireTools = function () {
    var self = this;
    this.el.file.addEventListener('change', function (e) {
      var f = e.target.files && e.target.files[0];
      if (f) self.addImage(f);
      e.target.value = '';
    });
    this.el.stage.addEventListener('dragover', function (e) { e.preventDefault(); self.el.stage.classList.add('is-drag'); });
    this.el.stage.addEventListener('dragleave', function () { self.el.stage.classList.remove('is-drag'); });
    this.el.stage.addEventListener('drop', function (e) {
      e.preventDefault();
      self.el.stage.classList.remove('is-drag');
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) self.addImage(f);
    });
    this.el.empty.addEventListener('click', function () { self.el.file.click(); });
    this.el.addText.addEventListener('click', function () { self.addText(); });
    this.el.center.addEventListener('click', function () { self.transform(function (o, r) { o.set({ left: r.left + r.width / 2, top: r.top + r.height / 2, originX: 'center', originY: 'center' }); }); });
    this.el.bigger.addEventListener('click', function () { self.transform(function (o) { o.scale((o.scaleX || 1) * 1.1); }); });
    this.el.smaller.addEventListener('click', function () { self.transform(function (o) { o.scale((o.scaleX || 1) / 1.1); }); });
    this.el.rotate.addEventListener('click', function () { self.transform(function (o) { o.rotate(((o.angle || 0) + ROTATE_STEP) % 360); }); });
    this.el.del.addEventListener('click', function () { self.removeActive(); });
    document.addEventListener('keydown', function (e) {
      var o = self.canvas.getActiveObject();
      if (!o || (o.isEditing)) return;
      var tag = (document.activeElement && document.activeElement.tagName) || '';
      if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); self.removeActive(); }
    });
    this.el.font.addEventListener('change', function () {
      var o = self.canvas.getActiveObject();
      if (o && o.epwKind === 'text') self.setFont(o, self.el.font.value);
    });
    this.el.inks.forEach(function (b) {
      b.addEventListener('click', function () { self.setInk(b.getAttribute('data-epw-ink')); });
    });
    if (this.el.inkCustom) this.el.inkCustom.addEventListener('input', function () { self.setInk(self.el.inkCustom.value); });
    if (this.el.proofAck) this.el.proofAck.addEventListener('change', function () { self.el.proof.classList.remove('needs-look'); self.refresh(); });
    this.el.add.addEventListener('click', function () { self.addToCart(); });
  };

  Designer.prototype.transform = function (fn) {
    var o = this.canvas.getActiveObject();
    if (!o) { this.status('Tap your design first, then use the tools.'); return; }
    fn(o, this.rect(this.zone(o.epwZone)));
    o.setCoords();
    this.keepInside(o);
    this.canvas.requestRenderAll();
    this.changed();
  };

  Designer.prototype.setFont = function (o, font) {
    var self = this;
    var apply = function () { o.set('fontFamily', font); o.initDimensions && o.initDimensions(); o.setCoords(); self.keepInside(o); self.canvas.requestRenderAll(); };
    if (document.fonts && document.fonts.load) document.fonts.load('40px "' + font + '"').then(apply, apply); else apply();
    this.changed();
  };

  Designer.prototype.setInk = function (hex) {
    this.ink = hex;
    this.el.inks.forEach(function (b) { b.classList.toggle('is-on', b.getAttribute('data-epw-ink') === hex); });
    var o = this.canvas.getActiveObject();
    if (o && o.epwKind === 'text') { o.set('fill', hex); this.canvas.requestRenderAll(); this.changed(); }
  };

  Designer.prototype.clipFor = function (zoneKey) {
    var r = this.rect(this.zone(zoneKey));
    return new fabric.Rect({ left: r.left, top: r.top, width: r.width, height: r.height, absolutePositioned: true });
  };

  Designer.prototype.place = function (o, kind) {
    var key = this.activeZone;
    o.epwZone = key;
    o.epwKind = kind;
    o.clipPath = this.clipFor(key);
    this.objects[key].push(o);
    this.canvas.add(o);
    this.canvas.setActiveObject(o);
    this.keepInside(o);
    this.canvas.requestRenderAll();
    this.changed();
  };

  Designer.prototype.addImage = function (file) {
    var self = this;
    if (!/^image\/(png|jpeg|svg\+xml|webp)$/.test(file.type)) { this.status('Use a PNG, JPG, SVG or WEBP image.', 'error'); return; }
    if (file.size > MAX_UPLOAD_BYTES) { this.status('That file is over 20 MB. Try a smaller one.', 'error'); return; }
    this.status('Loading your image…');
    var reader = new FileReader();
    reader.onload = function () {
      // Keep our own copy of the bytes: the file input is cleared after this.
      var copy = new File([reader.result], file.name, { type: file.type });
      var url = URL.createObjectURL(copy);
      var done = function (img) {
        if (!img || !img.width) { self.status('We couldn’t read that image. Try another file.', 'error'); return; }
        var r = self.rect(self.zone(self.activeZone));
        var s = Math.min(r.width / img.width, r.height / img.height) * 0.9;
        img.set({ left: r.left + r.width / 2, top: r.top + r.height / 2, originX: 'center', originY: 'center', scaleX: s, scaleY: s });
        img.epwFile = copy;
        self.place(img, 'image');
        self.status('Looking good. Drag to move, use the corners to resize.', 'ok');
      };
      if (file.type === 'image/svg+xml') {
        fabric.loadSVGFromURL(url, function (objs, opts) {
          var g = fabric.util.groupSVGElements(objs, opts);
          g.epwVector = true;
          done(g);
        });
      } else {
        fabric.Image.fromURL(url, done);
      }
    };
    reader.onerror = function () { self.status('We couldn’t read that file. Try again.', 'error'); };
    reader.readAsArrayBuffer(file);
  };

  Designer.prototype.addText = function () {
    var r = this.rect(this.zone(this.activeZone));
    var font = this.el.font.value || 'Anton';
    var ink = this.ink || (luminance(this.paint().fill) < 0.35 ? '#FFFFFF' : '#111111');
    var size = Math.max(14, Math.min(r.height * 0.3, r.width / 5));
    var t = new fabric.IText('YOUR TEXT', {
      left: r.left + r.width / 2, top: r.top + r.height / 2, originX: 'center', originY: 'center',
      fontFamily: font, fontSize: size, fill: ink, textAlign: 'center',
    });
    this.place(t, 'text');
    this.setFont(t, font);
    t.enterEditing();
    t.selectAll();
  };

  Designer.prototype.removeActive = function () {
    var o = this.canvas.getActiveObject();
    if (!o || !o.epwZone) return;
    var list = this.objects[o.epwZone];
    var i = list.indexOf(o);
    if (i >= 0) list.splice(i, 1);
    this.canvas.remove(o);
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
    this.changed();
  };

  Designer.prototype.wireCanvas = function () {
    var self = this;
    this.canvas.on('object:moving', function (e) { self.keepInside(e.target); });
    this.canvas.on('object:scaling', function (e) { self.keepInside(e.target); self.refreshDpi(); });
    this.canvas.on('object:rotating', function (e) { self.keepInside(e.target); });
    this.canvas.on('object:modified', function () { self.changed(); });
    this.canvas.on('text:changed', function () { self.changed(); });
    var sel = function () {
      var o = self.canvas.getActiveObject();
      var isText = !!(o && o.epwKind === 'text');
      self.el.textbar.hidden = !isText;
      if (isText) {
        self.el.font.value = o.fontFamily;
        self.el.inks.forEach(function (b) { b.classList.toggle('is-on', (b.getAttribute('data-epw-ink') || '').toLowerCase() === String(o.fill).toLowerCase()); });
      }
    };
    this.canvas.on('selection:created', sel);
    this.canvas.on('selection:updated', sel);
    this.canvas.on('selection:cleared', sel);
  };

  /** Keep a design inside its print spot: shrink it if it's too big, then nudge it back in. */
  Designer.prototype.keepInside = function (o) {
    if (!o || !o.epwZone) return;
    var r = this.rect(this.zone(o.epwZone));
    o.setCoords();
    var b = o.getBoundingRect(true, true);
    if (b.width > r.width + 0.5 || b.height > r.height + 0.5) {
      var k = Math.min(r.width / b.width, r.height / b.height);
      o.set({ scaleX: o.scaleX * k, scaleY: o.scaleY * k });
      o.setCoords();
      b = o.getBoundingRect(true, true);
    }
    var dx = 0, dy = 0;
    if (b.left < r.left) dx = r.left - b.left;
    if (b.top < r.top) dy = r.top - b.top;
    if (b.left + b.width > r.left + r.width) dx = r.left + r.width - (b.left + b.width);
    if (b.top + b.height > r.top + r.height) dy = r.top + r.height - (b.top + b.height);
    if (dx || dy) { o.set({ left: o.left + dx, top: o.top + dy }); o.setCoords(); }
  };

  // ---------------------------------------------------------------------------
  // Sayings ("Say What You Mean"): ready-made print art from the saying tee
  // ---------------------------------------------------------------------------
  /** Light letters on dark shirts, dark letters on light ones (same test as the text tool). */
  Designer.prototype.sayingInk = function () { return luminance(this.paint().fill) < 0.35 ? 'light' : 'dark'; };
  Designer.prototype.sayingBySlug = function (slug) {
    for (var i = 0; i < this.sayings.length; i++) if (this.sayings[i].slug === slug) return this.sayings[i];
    return null;
  };

  Designer.prototype.wireSayings = function () {
    var self = this;
    if (!this.sayings.length || !this.el.sayPanel || !this.el.sayGrid) {
      if (this.el.sayOpen) this.el.sayOpen.hidden = true;
      return;
    }
    var lastSet = null;
    this.sayings.forEach(function (sy) {
      if (sy.set && sy.set !== lastSet) {
        lastSet = sy.set;
        var h = document.createElement('p');
        h.className = 'epw-sayings__set';
        h.textContent = sy.set;
        self.el.sayGrid.appendChild(h);
      }
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'epw-saying';
      b.setAttribute('data-slug', sy.slug);
      var img = document.createElement('img');
      img.src = sy.thumb;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 120;
      img.height = 120;
      var t = document.createElement('span');
      t.textContent = sy.title;
      b.appendChild(img);
      b.appendChild(t);
      b.addEventListener('click', function () { self.addSaying(sy); self.showSayings(false); });
      self.el.sayGrid.appendChild(b);
    });
    if (this.el.sayOpen) this.el.sayOpen.addEventListener('click', function () {
      var open = self.el.sayPanel.hidden;
      self.showSayings(open);
      if (open && self.el.sayPanel.scrollIntoView) self.el.sayPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    if (this.el.sayClose) this.el.sayClose.addEventListener('click', function () { self.showSayings(false); if (self.el.sayOpen) self.el.sayOpen.focus(); });
  };

  Designer.prototype.showSayings = function (on) {
    if (!this.el.sayPanel) return;
    this.el.sayPanel.hidden = !on;
    if (this.el.sayOpen) this.el.sayOpen.setAttribute('aria-expanded', on ? 'true' : 'false');
    var cur = this.currentSaying();
    $all(this.el.sayGrid, '.epw-saying').forEach(function (b) { b.classList.toggle('is-on', !!cur && b.getAttribute('data-slug') === cur.epwSaying.slug); });
  };

  /** The saying already in the active print spot, if there is one. */
  Designer.prototype.currentSaying = function () {
    var list = this.objects[this.activeZone] || [];
    for (var i = list.length - 1; i >= 0; i--) if (list[i].epwSaying) return list[i];
    return null;
  };

  Designer.prototype.loadSayingImage = function (url, cb) {
    fabric.Image.fromURL(url, function (img, isError) { cb(isError || !img || !img.width ? null : img); }, { crossOrigin: 'anonymous' });
  };

  /**
   * Put a saying in the active print spot at its true print size (300 DPI),
   * shrunk to fit the spot. A saying already there is swapped in place.
   */
  Designer.prototype.addSaying = function (sy) {
    var self = this;
    var ink = this.sayingInk();
    var old = this.currentSaying();
    this.status('Loading “' + sy.title + '”…');
    this.loadSayingImage(sy[ink], function (img) {
      if (!img) { self.status('We couldn’t load that saying. Check your connection and try again.', 'error'); return; }
      var r = self.rect(self.zone(self.activeZone));
      var fit = Math.min(r.width / img.width, r.height / img.height) * 0.9;
      var s = Math.min(self.ppi / SAYING_DPI, fit);
      // New sayings sit high in the spot (about an inch down), where a chest print usually goes.
      var hgt = img.height * s;
      var at = { left: r.left + r.width / 2, top: Math.min(r.top + self.ppi + hgt / 2, r.top + r.height / 2), angle: 0 };
      if (old) {
        var c = old.getCenterPoint();
        at = { left: c.x, top: c.y, angle: old.angle || 0 };
        s = Math.min(old.getScaledWidth() / img.width, fit / 0.9);
        self.canvas.discardActiveObject();
        var list = self.objects[old.epwZone];
        list.splice(list.indexOf(old), 1);
        self.canvas.remove(old);
      }
      img.set({ left: at.left, top: at.top, angle: at.angle, originX: 'center', originY: 'center', scaleX: s, scaleY: s });
      img.epwSaying = sy;
      img.epwSayingInk = ink;
      self.place(img, 'image');
      self.status('“' + sy.title + '” is on. Drag to move, use the corners to resize.', 'ok');
    });
  };

  /** After a shirt color change, switch each saying to the letter color that shows up on it. */
  Designer.prototype.swapSayingInks = function () {
    var self = this;
    if (!this.sayings || !this.sayings.length) return;
    var ink = this.sayingInk();
    Object.keys(this.objects).forEach(function (k) {
      self.objects[k].forEach(function (o) {
        if (!o.epwSaying || o.epwSayingInk === ink) return;
        if (o.epwSaying.dark === o.epwSaying.light) { o.epwSayingInk = ink; return; } // full-color art: one file for every shirt
        var w = o.getScaledWidth();
        o.epwSayingInk = ink;
        self.loadSayingImage(o.epwSaying[ink], function (img) {
          if (!img || o.epwSayingInk !== ink) return;
          var el = img.getElement();
          var c = o.getCenterPoint();
          o.setElement(el);
          var sc = w / el.naturalWidth;
          o.set({ scaleX: sc, scaleY: sc });
          o.setPositionByOrigin(c, 'center', 'center');
          o.setCoords();
          self.keepInside(o);
          self.canvas.requestRenderAll();
          self.refresh();
        });
      });
    });
  };

  /** Start with the saying from the link (?saying=) or the one picked on the collection page. */
  Designer.prototype.preloadSaying = function () {
    if (!this.sayings.length) return;
    var slug = null;
    try { var q = new URLSearchParams(window.location.search); slug = q.get('design') || q.get('saying'); } catch (e) {}
    if (!slug) { try { slug = sessionStorage.getItem(SAYING_KEY); } catch (e) {} }
    var sy = slug ? this.sayingBySlug(slug) : null;
    if (sy) {
      try { sessionStorage.setItem(SAYING_KEY, sy.slug); } catch (e) {}
      this.selectZone(this.zones[0].key);
      this.addSaying(sy);
      return;
    }
    if (this.isSayingProduct) {
      this.showSayings(true);
      this.status('Pick a design to start, or upload your own art.');
    }
  };

  // ---------------------------------------------------------------------------
  // Checks and price
  // ---------------------------------------------------------------------------
  Designer.prototype.dpiOf = function (o) {
    if (o.epwKind !== 'image' || o.epwVector) return Infinity;
    return this.ppi / Math.max(o.scaleX || 1, o.scaleY || 1);
  };
  Designer.prototype.sizeIn = function (o) {
    var b = o.getBoundingRect(true, true);
    return { w: Math.round(b.width / this.ppi * 100) / 100, h: Math.round(b.height / this.ppi * 100) / 100 };
  };
  Designer.prototype.lowRes = function () {
    var self = this, worst = Infinity;
    Object.keys(this.objects).forEach(function (k) { self.objects[k].forEach(function (o) { worst = Math.min(worst, self.dpiOf(o)); }); });
    return worst < this.minDpi ? worst : null;
  };
  Designer.prototype.refreshDpi = function () {
    var self = this, worst = Infinity;
    (this.objects[this.activeZone] || []).forEach(function (o) { worst = Math.min(worst, self.dpiOf(o)); });
    if (worst < 150) {
      this.el.dpi.textContent = 'This image is small for this size and may print blurry. Make it smaller or upload a bigger file.';
      this.el.dpi.hidden = false;
    } else if (worst < 250) {
      this.el.dpi.textContent = 'Heads up: this image may print a little soft at this size.';
      this.el.dpi.hidden = false;
    } else {
      this.el.dpi.hidden = true;
    }
  };

  Designer.prototype.usedZones = function () {
    var self = this;
    return this.zones.filter(function (z) { return self.objects[z.key].length > 0; });
  };
  Designer.prototype.hitFor = function (z) {
    var a = z.widthIn * z.heightIn;
    var band = a <= AREA_SMALL ? 'small' : a <= AREA_MEDIUM ? 'medium' : 'large';
    var cents = { small: 400, medium: 525, large: 650 }[band];
    return { band: band, cents: cents, variantId: this.sur[band] };
  };
  Designer.prototype.sizeCharge = function (v) {
    var s = (this.sizeIndex >= 0 ? this.optionOf(v, this.sizeIndex) || '' : '').toUpperCase().replace(/\s+/g, '');
    if (/^(3XL|XXXL|[4-9]XL)/.test(s)) return { label: s + ' size', cents: 400, variantId: this.sur.xl3 };
    if (s === '2XL' || s === 'XXL') return { label: '2XL size', cents: 250, variantId: this.sur.xl2 };
    return null;
  };

  Designer.prototype.refresh = function () {
    var self = this;
    var v = this.variant();
    var used = this.usedZones();
    var hasPrimary = this.objects[this.zones[0].key].length > 0;
    var anyArt = used.length > 0;

    $all(this.el.tabs, '.epw-zone').forEach(function (b) {
      var z = self.zone(b.dataset.zone);
      var meta = b.querySelector('.epw-zone__meta');
      var n = self.objects[z.key].length;
      meta.textContent = self.isPrimary(z.key) ? (n ? 'Included ✓' : 'Included') : (n ? '+' + money(self.hitFor(z).cents, self.moneyFormat) + ' ✓' : '+' + money(self.hitFor(z).cents, self.moneyFormat));
      b.classList.toggle('has-art', n > 0);
    });
    this.el.empty.hidden = this.objects[this.activeZone].length > 0;
    this.refreshDpi();

    if (!v) {
      this.el.add.disabled = true;
      this.el.add.textContent = 'Sold out';
      return;
    }
    var base = v.price;
    if (this.el.unit) this.el.unit.textContent = money(base, this.moneyFormat);
    this.el.pBase.textContent = money(base, this.moneyFormat);
    var sc = this.sizeCharge(v);
    this.el.pSizeRow.hidden = !sc;
    if (sc) { this.el.pSizeLabel.textContent = sc.label; this.el.pSize.textContent = '+' + money(sc.cents, this.moneyFormat); }
    var extras = used.filter(function (z) { return !self.isPrimary(z.key); });
    var extraCents = 0;
    extras.forEach(function (z) { extraCents += self.hitFor(z).cents; });
    this.el.pExtraRow.hidden = extras.length === 0;
    if (extras.length) {
      this.el.pExtraLabel.textContent = 'Extra print: ' + extras.map(function (z) { return z.label.toLowerCase(); }).join(', ');
      this.el.pExtra.textContent = '+' + money(extraCents, this.moneyFormat);
    }
    var q = this.qty();
    var unit = base + (sc ? sc.cents : 0) + extraCents;
    this.el.pTotal.textContent = money(unit * q, this.moneyFormat);
    this.el.pTotalLabel.textContent = q > 1 ? 'Total (' + q + ' × ' + money(unit, this.moneyFormat) + ')' : 'Total';

    var approved = !this.el.proofAck || this.el.proofAck.checked;
    if (this.busy) return;
    if (!hasPrimary) {
      this.el.add.disabled = true;
      this.el.add.textContent = anyArt ? 'Add a ' + this.zones[0].label.toLowerCase() + ' design (it’s included)' : 'Add your design first';
    } else if (!approved) {
      this.el.add.disabled = false;
      this.el.add.textContent = 'Approve your proof to continue';
    } else {
      this.el.add.disabled = false;
      this.el.add.textContent = 'Add to cart · ' + money(unit * q, this.moneyFormat);
    }
  };

  Designer.prototype.changed = function () { this.withdrawProof(); this.refresh(); };
  Designer.prototype.withdrawProof = function () {
    if (this.el.proofAck && this.el.proofAck.checked) {
      this.el.proofAck.checked = false;
      this.status('You changed the design, so check it again and re-tick the proof box.');
    }
  };
  Designer.prototype.status = function (msg, tone) {
    this.el.status.textContent = msg || '';
    if (tone) this.el.status.setAttribute('data-tone', tone); else this.el.status.removeAttribute('data-tone');
  };

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------
  /** Print file for one spot: just that spot's art, transparent background, cropped to the spot. */
  Designer.prototype.exportZone = function (z) {
    var self = this;
    var r = this.rect(z);
    var saved = [];
    this.canvas.getObjects().forEach(function (o) { saved.push([o, o.visible]); o.visible = o.epwZone === z.key; });
    this.canvas.discardActiveObject();
    var worst = Infinity;
    this.objects[z.key].forEach(function (o) { worst = Math.min(worst, self.dpiOf(o)); });
    var dpi = Math.max(150, Math.min(this.minDpi, worst === Infinity ? this.minDpi : Math.ceil(worst)));
    var mult = dpi / this.ppi;
    var el = this.canvas.toCanvasElement(mult, { left: r.left, top: r.top, width: r.width, height: r.height });
    saved.forEach(function (s) { s[0].visible = s[1]; });
    this.canvas.requestRenderAll();
    return canvasToBlob(el, 'image/png');
  };

  /** What the customer approved: each side that has art, drawn on the garment. */
  Designer.prototype.exportMockup = function () {
    var self = this;
    var views = [];
    this.usedZones().forEach(function (z) { if (views.indexOf(z.view) < 0) views.push(z.view); });
    var size = 800, k = size / STAGE;
    var out = document.createElement('canvas');
    out.width = size * views.length;
    out.height = size;
    var ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    this.canvas.discardActiveObject();
    var jobs = views.map(function (view, i) {
      var holder = self.el.views.filter(function (v) { return v.getAttribute('data-view') === view; })[0];
      var svg = holder && holder.querySelector('svg');
      var svgUrl = svg ? self.svgDataUrl(svg) : null;
      var saved = [];
      self.canvas.getObjects().forEach(function (o) { saved.push([o, o.visible]); o.visible = !!o.epwZone && self.zone(o.epwZone).view === view; });
      var art = self.canvas.toCanvasElement(k);
      saved.forEach(function (s) { s[0].visible = s[1]; });
      return (svgUrl ? loadImage(svgUrl) : Promise.resolve(null)).then(function (img) {
        if (img) ctx.drawImage(img, i * size, 0, size, size);
        ctx.drawImage(art, i * size, 0, size, size);
      });
    });
    return Promise.all(jobs).then(function () {
      self.canvas.requestRenderAll();
      return canvasToBlob(out, 'image/jpeg', 0.88);
    });
  };

  /** Standalone copy of the garment drawing with its CSS colors written in. */
  Designer.prototype.svgDataUrl = function (svg) {
    var clone = svg.cloneNode(true);
    var src = svg.querySelectorAll('*');
    var dst = clone.querySelectorAll('*');
    for (var i = 0; i < src.length; i++) {
      var cs = window.getComputedStyle(src[i]);
      if (!cs) continue;
      ['fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'opacity', 'stroke-linecap'].forEach(function (p) {
        var val = cs.getPropertyValue(p);
        if (val) dst[i].setAttribute(p, val.replace(/url\("?(#[^")]+)"?\)/, 'url($1)'));
      });
      dst[i].removeAttribute('style');
      dst[i].removeAttribute('class');
    }
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', '640');
    clone.setAttribute('height', '640');
    clone.removeAttribute('style');
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(clone));
  };

  // ---------------------------------------------------------------------------
  // Cart
  // ---------------------------------------------------------------------------
  Designer.prototype.addToCart = function () {
    var self = this;
    var v = this.variant();
    var used = this.usedZones();
    if (!v) return;
    if (!this.objects[this.zones[0].key].length) { this.status('Add a design to the ' + this.zones[0].label.toLowerCase() + ' first.', 'error'); return; }
    if (this.el.proofAck && !this.el.proofAck.checked) {
      this.el.proof.classList.add('needs-look');
      this.el.proof.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.status('Check your design, then tick “This preview is my proof”.', 'error');
      return;
    }
    var low = this.lowRes();
    if (low !== null && low < 150 && !window.confirm('One of your images is small for the size you picked and may print blurry. Add it to the cart anyway?')) return;

    this.busy = true;
    this.el.add.disabled = true;
    this.el.add.textContent = 'Preparing your files…';
    this.status('Uploading your design. This can take a few seconds.');
    this.setGuidesVisible(false);

    var qty = this.qty();
    var approvedAt = new Date().toISOString();
    var terms = (this.el.proof && this.el.proof.getAttribute('data-terms')) || '';
    var primary = this.zones[0];
    var stamp = this.groupId.slice(0, 8);

    var lineFor = function (z, variantId, role, files) {
      var o = self.objects[z.key];
      var box = null;
      o.forEach(function (obj) {
        var b = obj.getBoundingRect(true, true);
        box = box ? { l: Math.min(box.l, b.left), t: Math.min(box.t, b.top), r: Math.max(box.r, b.left + b.width), b: Math.max(box.b, b.top + b.height) }
                  : { l: b.left, t: b.top, r: b.left + b.width, b: b.top + b.height };
      });
      var r = self.rect(z);
      var fd = new FormData();
      fd.append('id', String(variantId));
      fd.append('quantity', String(qty));
      fd.append('properties[Placement]', z.label);
      fd.append('properties[Print size]', (Math.round((box.r - box.l) / self.ppi * 10) / 10) + '" × ' + (Math.round((box.b - box.t) / self.ppi * 10) / 10) + '"');
      fd.append('properties[Print_Width_Inches]', String(Math.round((box.r - box.l) / self.ppi * 100) / 100));
      fd.append('properties[Print_Height_Inches]', String(Math.round((box.b - box.t) / self.ppi * 100) / 100));
      fd.append('properties[_Placement_Code]', z.code || z.key);
      fd.append('properties[_Offset_From_Top_Inches]', String(Math.round((box.t - r.top) / self.ppi * 100) / 100));
      fd.append('properties[_Offset_From_Center_Inches]', String(Math.round(((box.l + box.r) / 2 - (r.left + r.width / 2)) / self.ppi * 100) / 100));
      fd.append('properties[_Print_Method]', PRINT_METHOD);
      fd.append('properties[_Design_Group_Id]', self.groupId);
      fd.append('properties[_Line_Role]', role);
      fd.append('properties[_Artwork_URL]', files.art, 'design-' + stamp + '-' + z.key.toLowerCase() + '.png');
      var orig = o.filter(function (obj) { return obj.epwFile; })[0];
      if (orig) fd.append('properties[_Original_Artwork_URL]', orig.epwFile, orig.epwFile.name);
      var texts = o.filter(function (obj) { return obj.epwKind === 'text'; }).map(function (t) { return t.text + ' (' + t.fontFamily + ', ' + t.fill + ')'; });
      if (texts.length) fd.append('properties[Text]', texts.join(' | '));
      var says = o.filter(function (obj) { return obj.epwSaying; });
      if (says.length) {
        var byProp = {};
        says.forEach(function (x) { var k = x.epwSaying.prop || 'Saying'; (byProp[k] = byProp[k] || []).push(x.epwSaying.title); });
        Object.keys(byProp).forEach(function (k) { fd.append('properties[' + k + ']', byProp[k].join(' | ')); });
        fd.append('properties[_Saying_Slug]', says.map(function (x) { return x.epwSaying.slug + ':' + x.epwSayingInk; }).join(' | '));
      }
      return fd;
    };

    var arts = {};
    var chain = Promise.resolve();
    used.forEach(function (z) { chain = chain.then(function () { return self.exportZone(z).then(function (b) { arts[z.key] = b; }); }); });
    chain.then(function () { return self.exportMockup(); }).then(function (mock) {
      self.el.add.textContent = 'Adding to cart…';
      var fd = lineFor(primary, v.id, 'PRIMARY', { art: arts[primary.key] });
      fd.append('properties[Proof]', 'Approved on screen');
      fd.append('properties[_Proof_Approved_At]', approvedAt);
      fd.append('properties[_Proof_Terms]', terms);
      fd.append('properties[_Proof_Mockup_URL]', mock, 'proof-' + stamp + '.jpg');
      var posts = postForm('/cart/add.js', fd);
      used.filter(function (z) { return !self.isPrimary(z.key); }).forEach(function (z) {
        var hit = self.hitFor(z);
        posts = posts.then(function () { return postForm('/cart/add.js', lineFor(z, hit.variantId, 'SECONDARY_PLACEMENT', { art: arts[z.key] })); });
      });
      var sc = self.sizeCharge(v);
      if (sc && sc.variantId) {
        posts = posts.then(function () {
          var f = new FormData();
          f.append('id', String(sc.variantId));
          f.append('quantity', String(qty));
          f.append('properties[_Design_Group_Id]', self.groupId);
          f.append('properties[_Line_Role]', 'SIZE_UPCHARGE');
          f.append('properties[Applies to]', self.optionOf(v, self.sizeIndex));
          return postForm('/cart/add.js', f);
        });
      }
      return posts;
    }).then(function () {
      self.status('Added! Taking you to your cart…', 'ok');
      self.groupId = uuid();
      document.dispatchEvent(new CustomEvent('epw:added-to-cart'));
      try { sessionStorage.removeItem(SAYING_KEY); } catch (e) {}
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
    }).catch(function (err) {
      console.error('[EPW designer]', err);
      self.status('That didn’t go through: ' + (err && err.message ? err.message : 'please try again') + '.', 'error');
    }).then(function () {
      self.busy = false;
      self.setGuidesVisible(true);
      self.canvas.requestRenderAll();
      self.refresh();
    });
  };

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-epw-designer]'), function (root) {
      if (root.__epw) return;
      try { root.__epw = new Designer(root); } catch (e) { console.error('[EPW designer] failed to start', e); }
    });
  }
  function whenReady() {
    if (window.fabric) { start(); return; }
    var tries = 0;
    var t = setInterval(function () {
      if (window.fabric) { clearInterval(t); start(); }
      else if (++tries > 200) clearInterval(t);
    }, 50);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenReady); else whenReady();
  window.EpwDesigner = Designer;
})();
